import { useLayoutEffect, useRef, useCallback } from "react";
import type { AutoExpandConfig } from "../types/editor";

interface UseAutoExpandProps {
  containerId: string;
  autoExpandConfig?: AutoExpandConfig;
  editorInstance: any;
}

export const useAutoExpand = ({
  containerId,
  autoExpandConfig,
  editorInstance
}: UseAutoExpandProps) => {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateContainerHeight = useCallback(() => {
    if (!autoExpandConfig?.autoExpand) return;

    const container = document.getElementById(
      `editor-container-${containerId}`
    );
    if (!container) return;

    const editorElement = container.querySelector(
      ".ProseMirror"
    ) as HTMLElement | null;
    if (!editorElement) return;

    const scrollHeight = editorElement.scrollHeight;
    const { maxHeight } = autoExpandConfig;

    if (maxHeight && scrollHeight > maxHeight) {
      container.style.height = `${maxHeight}px`;
      container.style.overflowY = "auto";
    } else {
      container.style.height = `${scrollHeight + 2}px`;
      container.style.overflowY = "hidden";
    }
  }, [containerId, autoExpandConfig]);

  useLayoutEffect(() => {
    if (!autoExpandConfig?.autoExpand || !editorInstance) return;

    const container = document.getElementById(
      `editor-container-${containerId}`
    );
    if (!container) return;

    const editorElement = container.querySelector(
      ".ProseMirror"
    ) as HTMLElement | null;
    if (!editorElement) return;
    updateContainerHeight();
    resizeObserverRef.current = new ResizeObserver(() => {
      requestAnimationFrame(updateContainerHeight);
    });
    resizeObserverRef.current.observe(editorElement);
    const handleUpdate = () => requestAnimationFrame(updateContainerHeight);
    editorInstance.on("update", handleUpdate);
    editorInstance.on("focus", handleUpdate);

    return () => {
      resizeObserverRef.current?.disconnect();
      editorInstance.off("update", handleUpdate);
      editorInstance.off("focus", handleUpdate);
    };
  }, [
    containerId,
    autoExpandConfig?.autoExpand,
    editorInstance,
    updateContainerHeight
  ]);

  return { updateContainerHeight };
};
